import React, { useState, useEffect } from "react";
import { Tab } from "semantic-ui-react";
import { BasicModal } from "../../../components/Shared";
import { UserForm, ListUsers } from "../../../components/Admin/Users";
import { InviteUserForm } from "../../../components/Admin/Users/InviteUserForm";
import "./Users.scss";
import { useAuth } from "../../../hooks";
import {
  isAdmin,
  hasPermission,
  isMaster,
} from "../../../utils/checkPermission";
import { ErrorAccessDenied } from "../Error";
import { Permission } from "../../../api";
import { useLanguage } from "../../../contexts";

const permissionController = new Permission();

export function Users() {
  const [showModal, setShowModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [reload, setReload] = useState(false);
  const {
    accessToken,
    user: { role },
  } = useAuth();

  const [permissionsByRole, setPermissionsByRole] = useState([]);
  const onOpenCloseModal = () => setShowModal((prevState) => !prevState);
  const onOpenCloseInviteModal = () => setShowInviteModal((prevState) => !prevState);
  const onReload = () => setReload((prevState) => !prevState);

  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  useEffect(() => {
    (async () => {
      try {
        setPermissionsByRole([]);
        if (role) {
          const response = await permissionController.getPermissionsByRole(
            accessToken,
            role._id,
            true
          );
          setPermissionsByRole(response);
        }
      } catch (error) {
        console.error(error);
        setPermissionsByRole([]);
      }
    })();
  }, [role]);

  const canCreate = isMaster(role) || isAdmin(role) || hasPermission(permissionsByRole, role._id, "users", "create");
  const canInvite = isMaster(role) || isAdmin(role);

  // Tabs según el rol del usuario logueado
  const buildPanes = () => {
    const panes = [
      {
        menuItem: t("active_users"),
        render: () => (
          <Tab.Pane attached={false}>
            <ListUsers
              usersActive={true}
              reload={reload}
              onReload={onReload}
              onOpenModal={canCreate ? onOpenCloseModal : null}
            />
          </Tab.Pane>
        ),
      },
    ];

    if (isMaster(role) || isAdmin(role)) {
      panes.push({
        menuItem: t("inactive_users"),
        render: () => (
          <Tab.Pane attached={false}>
            <ListUsers
              usersActive={false}
              reload={reload}
              onReload={onReload}
            />
          </Tab.Pane>
        ),
      });
    }

    if (isMaster(role)) {
      panes.push({
        menuItem: t("pending_users"),
        render: () => (
          <Tab.Pane attached={false}>
            <ListUsers
              usersStatus="pending"
              reload={reload}
              onReload={onReload}
            />
          </Tab.Pane>
        ),
      });
    }

    if (isMaster(role) || isAdmin(role)) {
      panes.push({
        menuItem: t("invited_users"),
        render: () => (
          <Tab.Pane attached={false}>
            <ListUsers
              usersStatus="invited"
              reload={reload}
              onReload={onReload}
              onOpenInviteModal={canInvite ? onOpenCloseInviteModal : null}
            />
          </Tab.Pane>
        ),
      });
    }

    return panes;
  };

  if (
    isMaster(role) ||
    isAdmin(role) ||
    hasPermission(permissionsByRole, role._id, "users", "view")
  ) {
    return (
      <>
        <div className="users-page" style={{ padding: "0 0 1.5rem" }}>
          <div className="users-page-header-wrapper">
            <div className="users-page-header">
              <span className="users-page-title">{t("users")}</span>
            </div>
          </div>
          <Tab menu={{ secondary: true, pointing: true }} panes={buildPanes()} />
        </div>

        {/* Modal crear usuario manualmente */}
        <BasicModal
          show={showModal}
          close={onOpenCloseModal}
          title={null}
          size="small"
        >
          <UserForm close={onOpenCloseModal} onReload={onReload} />
        </BasicModal>

        {/* Modal invitar usuario por email */}
        <BasicModal
          show={showInviteModal}
          close={onOpenCloseInviteModal}
          title={t("invite_user")}
          size="small"
        >
          <InviteUserForm close={onOpenCloseInviteModal} onReload={onReload} />
        </BasicModal>
      </>
    );
  } else {
    return <ErrorAccessDenied />;
  }
}
